import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useJobStatus } from '../hooks/useJobStatus';
import { StepList } from '../components/status/StepList';
import { LogStream } from '../components/status/LogStream';

export function ProcessingPage() {
  const { jobId } = useParams<{ jobId: string }>();
  const navigate = useNavigate();
  /* Sprint 5 — when the upload was project-scoped, the upload page
     forwards `?return_to=/projects/AUDIT-YYYY-NNNN` so the breadcrumb
     and the "back to project" CTA stay anchored in the project flow
     rather than spilling into the Sprint-4 standalone listing. */
  const [searchParams] = useSearchParams();
  const returnTo = searchParams.get('return_to');

  const { status, isLoading, error, isConnected } = useJobStatus(jobId ?? '');

  /* Sprint 5 client feedback 2026-06-19: the prior auto-redirect (2.5s
     setTimeout that bounced to /projects/<auditId> on completion) was
     surprising — the analyst clicked "Processing" expecting to watch
     the pipeline finish, then was suddenly punted to the project
     workspace. Now: stay on the Processing page on completion, surface
     a prominent "Back to Project" CTA, and let the analyst navigate
     deliberately. */
  void returnTo; void navigate;  // setters retained for future use

  if (!jobId) {
    return <div className="page-body"><div className="alert alert-fail">Invalid job ID</div></div>;
  }

  if (isLoading) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', flex: 1 }}>
        <span className="spinner spinner-lg" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="page-body">
        <div className="alert alert-fail" role="alert">
          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
          {error}
        </div>
        <button className="btn btn-secondary" style={{ marginTop: 'var(--s3)' }} onClick={() => navigate(-1)}>
          Go Back
        </button>
      </div>
    );
  }

  if (!status) return null;

  const moduleClass = status.module_type === 'SPA' ? 'module-spa' : 'module-dd';
  const isTerminal = ['completed', 'failed', 'cancelled'].includes(status.status);
  const isRunning = status.status === 'processing';

  /* Pull project audit_id out of the return_to so we can show it in
     the banner below ("Project AUDIT-2026-0001"). Defensive: only the
     /projects/AUDIT-... pattern is recognised. */
  const projectAuditId = returnTo?.match(/^\/projects\/(AUDIT-[^/?#]+)/)?.[1];

  return (
    <div className={moduleClass}>
      <div className="module-page-header">
        {projectAuditId && (
          <div
            style={{
              padding: 'var(--s3) var(--s4)',
              background:
                status.module_type === 'SPA'
                  ? 'var(--spa-accent-bg)'
                  : 'var(--dd-accent-bg)',
              border:
                status.module_type === 'SPA'
                  ? '1px solid var(--spa-accent-border)'
                  : '1px solid var(--dd-accent-border)',
              borderRadius: 'var(--r-md)',
              marginBottom: 'var(--s4)',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}
          >
            <span style={{ fontSize: '0.9rem' }}>
              Processing Stage{' '}
              {status.module_type === 'SPA' ? '2 (SPA)' : '1 (DD)'} for
              project{' '}
              <strong style={{ fontFamily: 'var(--font-mono)' }}>
                {projectAuditId}
              </strong>
              {isTerminal && status.status === 'completed' && (
                <span style={{ marginLeft: 'var(--s3)', color: 'var(--pass)' }}>
                  · Complete
                </span>
              )}
              {isTerminal && status.status === 'failed' && (
                <span style={{ marginLeft: 'var(--s3)', color: 'var(--fail)' }}>
                  · Failed
                </span>
              )}
            </span>
            {/* "Back to Project" is now the analyst's only way back —
                no more auto-redirect after completion. Promote to
                btn-primary once the run is in a terminal state so it
                catches the eye as the natural next step. */}
            <button
              className={
                isTerminal && status.status === 'completed'
                  ? 'btn btn-primary'
                  : 'btn btn-ghost btn-sm'
              }
              onClick={() => navigate(returnTo || `/projects/${projectAuditId}`)}
            >
              ← Back to project
            </button>
          </div>
        )}
        <div className="breadcrumb">
          {projectAuditId ? (
            <>
              <a
                href="/projects"
                onClick={(e) => {
                  e.preventDefault();
                  navigate('/projects');
                }}
              >
                Projects
              </a>
              <span className="bc-sep">/</span>
              <a
                href={returnTo!}
                onClick={(e) => {
                  e.preventDefault();
                  navigate(returnTo!);
                }}
              >
                {projectAuditId}
              </a>
              <span className="bc-sep">/</span>
              <span>
                Stage {status.module_type === 'SPA' ? '2 (SPA)' : '1 (DD)'} ·
                Processing
              </span>
            </>
          ) : (
            <>
              <a href={status.module_type === 'SPA' ? '/spa/upload' : '/dd/upload'}>
                {status.module_type} Assessments
              </a>
              <span className="bc-sep">/</span>
              <span>Processing</span>
            </>
          )}
        </div>
        <div className="report-title-row">
          <div>
            <p className="module-eyebrow">{status.module_type} Module</p>
            <h1 className="t-h1">Processing Job</h1>
            <p className="t-xs t-muted" style={{ marginTop: 4 }}>{jobId}</p>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 'var(--s2)' }}>
            <span className={`badge ${status.module_type === 'SPA' ? 'badge-spa' : 'badge-dd'}`}>
              {status.module_type}
            </span>
            {!isConnected && !isTerminal && (
              <span className="t-xs t-muted">polling…</span>
            )}
          </div>
        </div>
      </div>

      <div className="page-body">
        <div className="proc-layout">
          <div className="proc-main">
            {/* Progress */}
            <div className="card">
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 'var(--s3)', gap: 'var(--s2)' }}>
                {/* Sprint 5 client feedback 2026-06-22 (Second-Set FE Issue 4):
                    fixed widths on the live-updating progress %% and
                    elapsed-time cells so digit-count changes during
                    polling (0 → 10 → 100) never reflow the parent. */}
                <span
                  className="t-sm"
                  style={{ fontWeight: 600, minWidth: '10ch', display: 'inline-block', fontVariantNumeric: 'tabular-nums' }}
                >
                  {status.progress_pct}% complete
                </span>
                <span
                  className="t-xs t-muted"
                  style={{ minWidth: '8ch', display: 'inline-block', textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}
                >
                  {status.elapsed_seconds}s elapsed
                </span>
              </div>
              <div className="progress-bar">
                <div className={`progress-fill${isRunning ? ' shimmer' : ''}`} style={{ width: `${status.progress_pct}%` }} />
              </div>
            </div>

            {/* Completion banners */}
            {status.status === 'completed' && (
              <div className="alert alert-pass">
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><polyline points="20 6 9 17 4 12"/></svg>
                <div style={{ flex: 1 }}>
                  <strong>Processing complete!</strong> All {status.total_steps} steps done.
                </div>
                <button
                  className="btn btn-primary btn-sm"
                  onClick={() => navigate(`/reports/${jobId}`)}
                >
                  View Report
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><line x1="5" y1="12" x2="19" y2="12"/><polyline points="12 5 19 12 12 19"/></svg>
                </button>
              </div>
            )}

            {status.status === 'failed' && (
              <div className="alert alert-fail">
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><circle cx="12" cy="12" r="10"/><line x1="15" y1="9" x2="9" y2="15"/><line x1="9" y1="9" x2="15" y2="15"/></svg>
                Processing failed. Please try uploading the document again.
              </div>
            )}

            {/* Pipeline Steps */}
            <div className="card">
              <div className="card-header">
                <h2 className="t-h3">Pipeline Steps</h2>
                <span className="badge badge-neutral">
                  {status.processing_steps.filter((s) => s.status === 'done').length} / {status.total_steps}
                </span>
              </div>
              <StepList steps={status.processing_steps} />
            </div>
          </div>

          {/* Sidebar */}
          <div className="proc-sidebar">
            {status.logs.length > 0 && (
              <div className="card card-sm">
                <p className="t-label t-label-mb3">Log Output</p>
                <LogStream logs={status.logs} />
              </div>
            )}
            <div className="card card-sm">
              <p className="t-label t-label-mb3">Status</p>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--s2)' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.875rem' }}>
                  <span className="t-muted">Module</span>
                  <span className={`module-chip ${status.module_type.toLowerCase()}`}>{status.module_type}</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.875rem' }}>
                  <span className="t-muted">State</span>
                  <span style={{ fontWeight: 600 }}>{status.status}</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.875rem' }}>
                  <span className="t-muted">Elapsed</span>
                  <span className="t-mono">{status.elapsed_seconds}s</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
